import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calendar, User, Eye, ArrowRight, Tag } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const BlogsPage = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { value: 'all', label: 'All Posts' },
    { value: 'adventure', label: 'Adventure' },
    { value: 'safety', label: 'Safety' },
    { value: 'locations', label: 'Locations' },
    { value: 'tips', label: 'Tips' },
    { value: 'news', label: 'News' }
  ];

  useEffect(() => {
    fetchBlogs();
  }, []);

  useEffect(() => {
    if (selectedCategory === 'all') {
      setFilteredBlogs(blogs);
    } else {
      setFilteredBlogs(blogs.filter(blog => blog.category === selectedCategory));
    }
  }, [selectedCategory, blogs]);

  const fetchBlogs = async () => {
    try {
      const response = await axios.get(`${API}/api/blogs`);
      setBlogs(response.data);
      setFilteredBlogs(response.data);
    } catch (error) {
      console.error('Failed to fetch blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      adventure: 'bg-red-500',
      safety: 'bg-green-500',
      locations: 'bg-blue-500',
      tips: 'bg-yellow-500',
      news: 'bg-purple-500'
    };
    return colors[category] || 'bg-gray-500';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-2xl">Loading blogs...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-yellow-500 to-red-600 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl md:text-6xl font-black text-white mb-4">Our Blog</h1>
          <p className="text-xl text-white/90 max-w-2xl">
            Stories, tips, and insights from the world of bungy jumping and adventure sports
          </p>
        </div>
      </div>

      {/* Category Filter */}
      <div className="bg-gray-900 border-b border-gray-800 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex overflow-x-auto space-x-3 scrollbar-hide">
            {categories.map((category) => (
              <button
                key={category.value}
                onClick={() => setSelectedCategory(category.value)}
                className={`px-6 py-2 rounded-full font-bold whitespace-nowrap transition-all ${
                  selectedCategory === category.value
                    ? 'bg-yellow-400 text-black'
                    : 'bg-gray-800 text-white hover:bg-gray-700'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Blog Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {filteredBlogs.length === 0 ? (
          <div className="text-center py-20">
            <h2 className="text-3xl font-bold text-white mb-4">No blogs found</h2>
            <p className="text-gray-400">Check back later for new content!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredBlogs.map((blog) => (
              <article
                key={blog._id}
                onClick={() => navigate(`/blog/${blog.slug}`)}
                className="group bg-gray-900 rounded-xl overflow-hidden border border-gray-800 hover:border-yellow-400 transition-all cursor-pointer transform hover:scale-105 duration-300"
              >
                {/* Featured Image */}
                {blog.featuredImage ? (
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={blog.featuredImage}
                      alt={blog.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute top-4 left-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold text-white ${getCategoryColor(blog.category)}`}>
                        {blog.category.toUpperCase()}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="relative h-48 bg-gradient-to-br from-yellow-500 to-red-600 flex items-center justify-center">
                    <span className={`absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-bold text-white ${getCategoryColor(blog.category)}`}>
                      {blog.category.toUpperCase()}
                    </span>
                    <h3 className="text-4xl font-black text-white opacity-20">INDYA BUNGY</h3>
                  </div>
                )}

                {/* Content */}
                <div className="p-6">
                  <h2 className="text-2xl font-bold text-white mb-3 group-hover:text-yellow-400 transition-colors">
                    {blog.title}
                  </h2>

                  {blog.excerpt && (
                    <p className="text-gray-400 mb-4 line-clamp-3">{blog.excerpt}</p>
                  )}

                  {/* Meta Info */}
                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-4">
                    <div className="flex items-center space-x-1">
                      <User size={16} />
                      <span>{blog.author}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Calendar size={16} />
                      <span>{formatDate(blog.publishedAt || blog.createdAt)}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Eye size={16} />
                      <span>{blog.views} views</span>
                    </div>
                  </div>

                  {/* Tags */}
                  {blog.tags && blog.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {blog.tags.slice(0, 3).map((tag, index) => (
                        <span key={index} className="flex items-center space-x-1 px-2 py-1 bg-gray-800 rounded text-xs text-gray-400">
                          <Tag size={12} />
                          <span>{tag}</span>
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Read More */}
                  <div className="flex items-center space-x-2 text-yellow-400 font-bold group-hover:space-x-4 transition-all">
                    <span>Read More</span>
                    <ArrowRight size={20} className="group-hover:translate-x-2 transition-transform" />
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogsPage;